import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { Link as RouterLink } from 'react-router-dom';
import { listFacilities, type PublicFacility } from '../../lib/facilities-api';
import { extractApiError } from '../../lib/api-client';
import { useT } from '../../i18n';
import { FacilityTypeChip } from './type-chip';

export function FacilitiesListPage() {
  const t = useT();
  const [items, setItems] = useState<PublicFacility[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const { items } = await listFacilities({ limit: 50 });
      setItems(items);
    } catch (e) {
      setError(extractApiError(e).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <div>
          <Typography variant="h4" fontWeight={600}>
            {t('fac.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('fac.subtitle')}
          </Typography>
        </div>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          component={RouterLink}
          to="/facilities/new"
        >
          {t('fac.newFacility')}
        </Button>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Card variant="outlined">
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {loading ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : items.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Typography color="text.secondary">{t('fac.empty')}</Typography>
              <Button
                variant="text"
                component={RouterLink}
                to="/facilities/new"
                sx={{ mt: 1 }}
              >
                {t('fac.addFirst')}
              </Button>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('common.colName')}</TableCell>
                    <TableCell sx={{ width: 130 }}>{t('fac.fieldType')}</TableCell>
                    <TableCell sx={{ width: 110 }}>{t('fac.colHours')}</TableCell>
                    <TableCell align="right" sx={{ width: 90 }}>
                      {t('fac.colSlot')}
                    </TableCell>
                    <TableCell sx={{ width: 150 }}>{t('fac.colApproval')}</TableCell>
                    <TableCell sx={{ width: 110 }}>{t('common.status')}</TableCell>
                    <TableCell align="right" sx={{ width: 80 }}>
                      {t('common.actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((f) => (
                    <TableRow key={f.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {f.name}
                        </Typography>
                        {f.description ? (
                          <Typography variant="caption" color="text.secondary">
                            {f.description.slice(0, 60)}
                            {f.description.length > 60 ? '…' : ''}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <FacilityTypeChip type={f.type} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {f.open_time && f.close_time
                            ? `${f.open_time}–${f.close_time}`
                            : t('fac.hours24h')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {f.requires_booking ? (
                          `${f.slot_minutes}m`
                        ) : (
                          <Chip label={t('fac.walkIn')} size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        {f.requires_booking ? (
                          <Chip
                            label={f.requires_approval ? t('fac.approvalRequired') : t('fac.approvalAuto')}
                            size="small"
                            color={f.requires_approval ? 'warning' : 'success'}
                            variant="outlined"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={f.is_active ? t('fac.statusActive') : t('fac.statusHidden')}
                          size="small"
                          color={f.is_active ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          title={t('common.edit')}
                          component={RouterLink}
                          to={`/facilities/${f.id}/edit`}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}
